import type { layout, stats } from "@app/domain";
import type { SeatInfo } from "@app/protocol";
import { type ReactNode, type RefObject, useRef } from "react";
import { useT } from "../i18n/context";
import { Ornament } from "./Ornament";
import type { CardAction } from "./PlayerCard";
import { Badge, EmptySeat, Face, HandBacks, type PodBadge, type TurnClock } from "./PodParts";
import { frameReach, RankFrameImage } from "./RankFrame";

export type { PodBadge, TurnClock };
export type PillTone = "light" | "dark" | "alert";

/** What one seat shows: the status line and its tone, a badge, the turn clock, what a tap does. */
export interface SeatLook {
  status: string;
  tone: PillTone;
  badge?: PodBadge | undefined;
  /** The turn clock while this seat acts; absent hides it. */
  clock?: TurnClock | null | undefined;
  /** Cards still in this seat's hand, drawn as little backs; absent where the game does not show them. */
  hand?: number | undefined;
  /** An empty seat you may take. */
  onSit?: (() => void) | undefined;
  /** The owner's action on someone else's card: "Remove from table" (review 0017 decision 99). */
  action?: CardAction | undefined;
}

const PILL: Record<PillTone | "empty", string> = {
  light: "bg-ink text-ground",
  dark: "border border-edge bg-ground/55 text-ink",
  alert: "bg-danger text-ink",
  empty: "border border-ink/25 bg-ground/45 text-ink",
};

/** The seat's card, open or not; the pod renders it and says so on its button. */
export interface PodCardSlot {
  open: boolean;
  onToggle: () => void;
  /** The card itself while open; it gets the pod's button to hand focus back to. */
  render: (opener: RefObject<HTMLButtonElement | null>) => ReactNode;
}

/** The circle: an empty seat, a bot's face, or a person's face as the button that opens their card. */
function Circle({
  seat,
  look,
  size,
  card,
}: {
  seat: SeatInfo | null;
  look: SeatLook;
  size: number;
  card: PodCardSlot;
}) {
  const { t } = useT();
  const opener = useRef<HTMLButtonElement>(null);
  if (seat === null)
    return <EmptySeat size={size} label={t("lobby.seat.empty")} onSit={look.onSit} />;
  const face = <Face seat={seat} size={size} clock={look.clock ?? null} />;
  // Bots have no record and no card (review 0017 S9).
  if (seat.bot) return face;
  return (
    <>
      <button
        ref={opener}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={card.open}
        aria-label={seat.name}
        className="block rounded-full"
        onClick={card.onToggle}
      >
        {face}
      </button>
      {card.open ? card.render(opener) : null}
    </>
  );
}

/**
 * One seat (handoff: oyuncu podu): avatar or empty circle, status pill below.
 * No name on the table (owner, 11 Sept); screen readers still hear it.
 */
export function Pod({
  seat,
  look,
  frame,
  dense,
  card,
  rank = null,
  overlay,
}: {
  seat: SeatInfo | null;
  look: SeatLook;
  frame: layout.PodFrame;
  dense: boolean;
  card: PodCardSlot;
  /** The player's rank frame around the circle, larger than it (review 0014). */
  rank?: stats.RankFrame | null;
  /** Drawn on the circle: the gift the player holds and their latest reaction (review 0022). */
  overlay?: ReactNode;
}) {
  const { size } = frame;
  const pill = dense ? "px-2.5 py-0.5 text-[12px]" : "px-3 py-1 text-[14px]";
  const framed = seat !== null && rank !== null;
  // Framed, the pill hangs under the frame, so the I-II-III medallion stays visible (owner, 13 Sept).
  const pillAt = framed ? { top: size + frameReach(size) - 6 } : { bottom: -frame.pillDrop };
  return (
    <div className="flex h-full w-full flex-col items-center">
      {seat ? <span className="sr-only">{seat.name}</span> : null}
      <div data-circle className="relative flex-none" style={{ width: size, height: size }}>
        {/* Behind the avatar: the hand it still holds. */}
        {look.hand ? <HandBacks count={look.hand} size={size} /> : null}
        <Circle seat={seat} look={look} size={size} card={card} />
        {framed ? (
          <Ornament>
            <RankFrameImage rank={rank} avatarSize={size} />
          </Ornament>
        ) : null}
        {look.badge ? <Badge badge={look.badge} size={size} /> : null}
        {overlay}
        <span
          className={`pointer-events-none absolute left-1/2 -translate-x-1/2 whitespace-nowrap rounded-pill font-semibold ${pill} ${PILL[seat === null ? "empty" : look.tone]}`}
          style={pillAt}
        >
          {look.status}
        </span>
      </div>
    </div>
  );
}
